import * as React from "react"

import { Action } from "@tenforce/toolbox-button-push-react"
import { FieldItems, ReadOnly } from "@tenforce/toolbox-field-react"
import { Cell, RowContent } from "@tenforce/toolbox-table-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { formatString } from "../../../util/Strings"
import FileDiff from "../../diff/FileDiff"
import { Labels } from "../../../util/Translations"
import { IAddin } from "../index"
import { ITenForceWindow } from "../../../global"

declare let window: ITenForceWindow

interface Props {
    addin: IAddin
    requestAddins: () => void
}

interface State {
    opened: boolean
    repairLoading: boolean
}

export default class Row extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            opened: false,
            repairLoading: false,
        }
    }

    render(): JSX.Element {
        const { addin } = this.props
        const areThereChanges = addin.diff.changes.length > 0

        const installationTime = !!addin.diff.installationTime
            ? new Date(addin.diff.installationTime).toLocaleString("en-GB")
            : "-"
        const lastChangeTime = !!addin.diff.lastChangeTime
            ? new Date(addin.diff.lastChangeTime).toLocaleString("en-GB")
            : "-"

        const { NoChangedFiles, SeeNChangedFiles } = Labels
        const { Repair } = Labels
        return (
            <>
                <RowContent
                    extraClasses={this.state.repairLoading ? " is-loading" : ""}
                >
                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={addin.addInName}
                                title={addin.addInName}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={addin.addInVersion}
                                title={addin.addInVersion}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <Action
                            data-ft-deployment="addin-see-changes"
                            extraClasses="tw-pl-6"
                            isDisabled={!areThereChanges}
                            isReadOnly={!areThereChanges}
                            onClick={this.toggleOpen}
                        >
                            {areThereChanges ? (
                                <>
                                    {this.state.opened
                                        ? Symbols.ExpandedBelow
                                        : Symbols.Expand}
                                    &nbsp;
                                    {formatString(
                                        SeeNChangedFiles,
                                        addin.diff.changes.length,
                                    )}
                                </>
                            ) : (
                                NoChangedFiles
                            )}
                        </Action>
                    </Cell>

                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={installationTime}
                                title={installationTime}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <FieldItems>
                            <ReadOnly
                                value={lastChangeTime}
                                title={lastChangeTime}
                            />
                        </FieldItems>
                    </Cell>

                    <Cell>
                        <Action
                            data-ft-deployment="addin-repair"
                            extraClasses="tw-pl-6"
                            onClick={this.repair}
                            title={Repair}
                        >
                            {Symbols.Wrench}
                        </Action>
                    </Cell>
                </RowContent>

                {this.state.opened && addin.diff.changes.length > 0 && (
                    <ul className="tw-m-3 tw-border-2 tw-border-solid tw-border-yellow-700">
                        {addin.diff.changes.map((change, i) => (
                            <li key={`${addin.addInName}@${i}`}>
                                <FileDiff {...change} />
                            </li>
                        ))}
                    </ul>
                )}
            </>
        )
    }

    toggleOpen = (): void => {
        this.setState({
            opened: !this.state.opened,
        })
    }

    repair = (): void => {
        this.setState({ repairLoading: true })
        const { addInId, addInVersion } = this.props.addin
        const Overlay = window.Overlay
        const overlay = new Overlay(Overlay.large)
        overlay.setAfterLoad(() => this.setState({ repairLoading: false }))
        overlay.setAfterClose((submit: boolean) =>
            this.repairOverlayClosed(submit),
        )
        overlay.load(
            window.TenForce.Execution.Web.Ajax.Service.ShowAddInPreviewOverlay,
            "repair",
            addInId,
            addInVersion,
        )
    }

    repairOverlayClosed = (submit: boolean): void => {
        if (submit && !!window.pageController) {
            this.props.requestAddins()
            window.pageController.gdlAvailableAddIns.ManagingObject.getPage()
        }
    }
}
